import { useState, useEffect, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { downloadProtectedFile } from "../utils/openBlob";

const STATUS_CONFIG = {
  pending:        { label: "Attente",    color: "#f59e0b" },
  validated:      { label: "Validée",    color: "#3b82f6" },
  in_preparation: { label: "En prépa",  color: "#8b5cf6" },
  ready:          { label: "Prête",      color: "#06b6d4" },
  shipped:        { label: "Expédiée",  color: "#1a9e72" },
  delivered:      { label: "Livrée",    color: "#22c55e" },
  cancelled:      { label: "Annulée",   color: "#ef4444" },
};

const SOURCE_CONFIG = {
  manual:   { label: "Manuelle",  color: "#4b5563" },
  excel:    { label: "Excel",     color: "#1a9e72" },
  pwa:      { label: "PWA",       color: "#185fa5" },
  ocr_pdf:  { label: "OCR PDF",   color: "#8b5cf6" },
  api:      { label: "API",       color: "#f59e0b" },
};

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", width: "100%", fontSize: 13 },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "16px 20px", flex: 1 },
};

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtInt(n) { return (n || 0).toLocaleString("fr-FR"); }

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { label: status, color: "#4b5563" };
  return (
    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: c.color + "22", color: c.color }}>{c.label}</span>
  );
}

function SourceBadge({ source }) {
  const c = SOURCE_CONFIG[source] || { label: source, color: "#4b5563" };
  return (
    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700, background: c.color + "22", color: c.color }}>{c.label}</span>
  );
}

export default function Commandes({ currentCompanyId, currentSociete, onNavigate }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [toast, setToast] = useState(null);

  // New order form
  const [form, setForm] = useState({ client_name: "", client_email: "", delivery_address: "", order_date: new Date().toISOString().slice(0, 10), expected_delivery: "", notes: "" });
  const [lines, setLines] = useState([{ product_name: "", quantity_ordered: 1, unit_price: 0, tva_rate: 20 }]);
  const [submitting, setSubmitting] = useState(false);

  // Import states
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrForm, setOcrForm] = useState(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const apiCall = useCallback(async (method, path, body = null) => {
    const res = await fetch("/api/customer-orders" + path, {
      method,
      headers: { Authorization: "Bearer " + getToken(), "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const loadStats = useCallback(async () => {
    if (!currentCompanyId) return;
    try {
      const d = await apiCall("GET", `/stats?company_id=${currentCompanyId}`);
      setStats(d);
    } catch (e) { console.error(e); }
  }, [currentCompanyId, apiCall]);

  const loadOrders = useCallback(async () => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: currentCompanyId, limit: 100 });
      if (filterStatus) qs.set("status", filterStatus);
      if (search) qs.set("search", search);
      const d = await apiCall("GET", `/?${qs}`);
      setOrders(d.orders || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [currentCompanyId, filterStatus, search, apiCall]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === "list") loadOrders(); }, [tab, loadOrders]);

  const openDetail = async (order) => {
    try {
      const d = await apiCall("GET", `/${order.id}`);
      setDetailOrder(d);
    } catch (e) { setDetailOrder(order); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    try {
      await apiCall("DELETE", `/${id}`);
      showToast("Commande supprimée");
      loadOrders();
      loadStats();
    } catch (e) { showToast("Erreur lors de la suppression", "error"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await apiCall("PATCH", `/${id}/status`, { status });
      showToast("Statut mis à jour");
      if (detailOrder?.id === id) setDetailOrder(o => ({ ...o, status }));
      loadOrders();
      loadStats();
    } catch (e) { showToast("Erreur", "error"); }
  };

  const createBL = async (id) => {
    try {
      await apiCall("POST", `/${id}/create-preparation`);
      showToast("Bon de livraison créé");
    } catch (e) { showToast("Erreur création BL", "error"); }
  };

  // Lines calculations
  const totalHT = lines.reduce((s, l) => s + (parseFloat(l.unit_price) || 0) * (parseInt(l.quantity_ordered) || 0), 0);
  const totalTVA = lines.reduce((s, l) => s + (parseFloat(l.unit_price) || 0) * (parseInt(l.quantity_ordered) || 0) * ((parseFloat(l.tva_rate) || 20) / 100), 0);

  const submitOrder = async () => {
    if (!form.client_name || !form.order_date) return showToast("Nom client et date requis", "error");
    if (lines.some(l => !l.product_name || !l.unit_price)) return showToast("Remplissez toutes les lignes", "error");
    setSubmitting(true);
    try {
      await apiCall("POST", "/", { ...form, company_id: currentCompanyId, source: "manual", lines });
      showToast("Commande créée");
      setForm({ client_name: "", client_email: "", delivery_address: "", order_date: new Date().toISOString().slice(0, 10), expected_delivery: "", notes: "" });
      setLines([{ product_name: "", quantity_ordered: 1, unit_price: 0, tva_rate: 20 }]);
      setTab("list");
      loadStats();
    } catch (e) { showToast("Erreur création commande", "error"); } finally { setSubmitting(false); }
  };

  const uploadImport = async () => {
    if (!importFile) return showToast("Sélectionnez un fichier Excel", "error");
    const fd = new FormData();
    fd.append("file", importFile);
    fd.append("company_id", currentCompanyId);
    try {
      const res = await fetch("/api/customer-orders/import-excel", { method: "POST", headers: { Authorization: "Bearer " + getToken() }, body: fd });
      const d = await res.json();
      setImportResult(d);
      showToast(`${d.imported || 0} commandes importées`);
      loadStats();
    } catch (e) { showToast("Erreur import Excel", "error"); }
  };

  const uploadOCR = async () => {
    if (!ocrFile) return showToast("Sélectionnez un PDF", "error");
    const fd = new FormData();
    fd.append("file", ocrFile);
    fd.append("company_id", currentCompanyId);
    try {
      const res = await fetch("/api/customer-orders/ocr-pdf", { method: "POST", headers: { Authorization: "Bearer " + getToken() }, body: fd });
      const d = await res.json();
      setOcrResult(d);
      if (d.order) {
        setOcrForm({ ...d.order, company_id: currentCompanyId, source: "ocr_pdf", order_date: d.order.order_date || new Date().toISOString().slice(0, 10) });
      }
      showToast("PDF analysé avec succès");
    } catch (e) { showToast("Erreur analyse OCR", "error"); }
  };

  const confirmOCR = async () => {
    if (!ocrForm) return;
    try {
      await apiCall("POST", "/", ocrForm);
      showToast("Commande créée depuis OCR");
      setOcrFile(null); setOcrResult(null); setOcrForm(null);
      setTab("list"); loadStats();
    } catch (e) { showToast("Erreur création", "error"); }
  };

  const TABS = [
    { id: "dashboard", label: "📊 Tableau de bord" },
    { id: "list",      label: "📋 Commandes" },
    { id: "new",       label: "➕ Nouvelle" },
    { id: "excel",     label: "📥 Import Excel" },
    { id: "ocr",       label: "🤖 OCR PDF" },
  ];

  const statusChartData = stats ? Object.entries(STATUS_CONFIG).map(([k, v]) => ({
    name: v.label, count: stats[k] || 0, color: v.color,
  })).filter(d => d.count > 0) : [];

  const pagedOrders = orders.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(orders.length / 10);

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>📦 Commandes clients</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete} — gestion multi-canaux</div>
        </div>
        <button onClick={() => setTab("new")} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Nouvelle commande</button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb", paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {/* ── Tab: Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Total commandes", value: fmtInt(stats?.total), color: "#111827" },
              { label: "CA total HT", value: fmt(stats?.total_ht) + " €", color: "#1a9e72" },
              { label: "En cours", value: fmtInt((stats?.pending || 0) + (stats?.validated || 0) + (stats?.in_preparation || 0)), color: "#f59e0b" },
              { label: "Livrées", value: fmtInt(stats?.delivered), color: "#22c55e" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 160 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value ?? "—"}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Commandes par statut</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statusChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fill: "#4b5563", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#4b5563", fontSize: 11 }} width={70} />
                  <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} labelStyle={{ color: "#111827" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {statusChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Dernières commandes</div>
              {orders.length === 0 && !loading && <div style={{ color: "#4b5563", fontSize: 13 }}>Aucune commande</div>}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    {["N°", "Client", "Montant HT", "Statut"].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: "#4b5563", fontWeight: 600 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, 6).map(o => (
                    <tr key={o.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 8px", color: "#4b5563" }}>{o.order_number}</td>
                      <td style={{ padding: "6px 8px", color: "#111827", fontWeight: 600 }}>{o.client_name}</td>
                      <td style={{ padding: "6px 8px", color: "#1a9e72", fontWeight: 700 }}>{fmt(o.total_ht)} €</td>
                      <td style={{ padding: "6px 8px" }}><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => setTab("list")} style={{ marginTop: 12, ...S.btn, background: "rgba(26,158,114,0.15)", color: "#1a9e72", fontSize: 12 }}>Voir toutes →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: List ── */}
      {tab === "list" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <input placeholder="🔍 Rechercher N° ou client…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ ...S.input, width: 260 }} />
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
              style={{ ...S.input, width: 160 }}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={loadOrders} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>🔄 Actualiser</button>
          </div>

          {loading ? <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div> : (
            <div style={S.card}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    {["N° commande", "Client", "Date", "Montant HT", "Source", "Statut", "Actions"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedOrders.map(o => (
                    <tr key={o.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "10px 12px", color: "#4b5563", fontWeight: 600 }}>{o.order_number}</td>
                      <td style={{ padding: "10px 12px", color: "#111827", fontWeight: 600 }}>{o.client_name}</td>
                      <td style={{ padding: "10px 12px", color: "#4b5563" }}>{o.order_date}</td>
                      <td style={{ padding: "10px 12px", color: "#1a9e72", fontWeight: 700 }}>{fmt(o.total_ht)} €</td>
                      <td style={{ padding: "10px 12px" }}><SourceBadge source={o.source} /></td>
                      <td style={{ padding: "10px 12px" }}><StatusBadge status={o.status} /></td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openDetail(o)} style={{ ...S.btn, padding: "4px 10px", background: "rgba(24,95,165,0.2)", color: "#3b82f6", fontSize: 12 }}>👁</button>
                          <button onClick={() => deleteOrder(o.id)} style={{ ...S.btn, padding: "4px 10px", background: "rgba(239,68,68,0.15)", color: "#ef4444", fontSize: 12 }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pagedOrders.length === 0 && <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#4b5563" }}>Aucune commande trouvée</td></tr>}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563", opacity: page === 1 ? 0.4 : 1 }}>← Préc.</button>
                  <span style={{ color: "#4b5563", fontSize: 13, lineHeight: "34px" }}>Page {page} / {totalPages}</span>
                  <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563", opacity: page === totalPages ? 0.4 : 1 }}>Suiv. →</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Detail Modal ── */}
      {detailOrder && (
        <div onClick={e => e.target === e.currentTarget && setDetailOrder(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#ffffff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 700, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: "#111827" }}>{detailOrder.order_number}</h3>
                <div style={{ color: "#4b5563", fontSize: 12, marginTop: 4 }}>{detailOrder.client_name} · {detailOrder.order_date}</div>
              </div>
              <button onClick={() => setDetailOrder(null)} style={{ ...S.btn, background: "transparent", color: "#4b5563", fontSize: 18 }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div><div style={{ color: "#4b5563", fontSize: 11 }}>Email client</div><div style={{ color: "#111827", fontSize: 13 }}>{detailOrder.client_email || "—"}</div></div>
              <div><div style={{ color: "#4b5563", fontSize: 11 }}>Adresse livraison</div><div style={{ color: "#111827", fontSize: 13 }}>{detailOrder.delivery_address || "—"}</div></div>
              <div><div style={{ color: "#4b5563", fontSize: 11 }}>Livraison prévue</div><div style={{ color: "#111827", fontSize: 13 }}>{detailOrder.expected_delivery || "—"}</div></div>
              <div><div style={{ color: "#4b5563", fontSize: 11 }}>Source</div><div style={{ marginTop: 2 }}><SourceBadge source={detailOrder.source} /></div></div>
            </div>

            {detailOrder.lines?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: "#4b5563", fontSize: 11, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Lignes de commande</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    {["Produit", "Qté", "P.U. HT", "TVA", "Total HT"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: "#4b5563" }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {detailOrder.lines.map((l, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 8px", color: "#111827" }}>{l.product_name}</td>
                        <td style={{ padding: "6px 8px", color: "#4b5563" }}>{l.quantity_ordered}</td>
                        <td style={{ padding: "6px 8px", color: "#111827" }}>{fmt(l.unit_price)} €</td>
                        <td style={{ padding: "6px 8px", color: "#4b5563" }}>{l.tva_rate}%</td>
                        <td style={{ padding: "6px 8px", color: "#1a9e72", fontWeight: 700 }}>{fmt((l.unit_price || 0) * (l.quantity_ordered || 0))} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ textAlign: "right", marginTop: 10, color: "#1a9e72", fontWeight: 800, fontSize: 15 }}>Total HT : {fmt(detailOrder.total_ht)} €</div>
              </div>
            )}

            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <select value={detailOrder.status} onChange={e => changeStatus(detailOrder.id, e.target.value)}
                style={{ ...S.input, width: "auto" }}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button onClick={() => createBL(detailOrder.id)} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>📋 Créer BL</button>
              <button onClick={() => setDetailOrder(null)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: New ── */}
      {tab === "new" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 20px", color: "#111827" }}>Nouvelle commande</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            {[
              { label: "Nom du client *", key: "client_name", type: "text" },
              { label: "Email client", key: "client_email", type: "email" },
              { label: "Date de commande *", key: "order_date", type: "date" },
              { label: "Livraison prévue", key: "expected_delivery", type: "date" },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 6 }}>{f.label}</label>
                <input type={f.type} value={form[f.key]} onChange={e => setForm(o => ({ ...o, [f.key]: e.target.value }))} style={S.input} />
              </div>
            ))}
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 6 }}>Adresse de livraison</label>
              <input value={form.delivery_address} onChange={e => setForm(o => ({ ...o, delivery_address: e.target.value }))} style={S.input} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "#4b5563", fontSize: 11, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>Lignes produits</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                {["Produit *", "Quantité", "P.U. HT (€) *", "TVA %", ""].map((h, i) => <th key={i} style={{ padding: "6px 8px", textAlign: "left", color: "#4b5563" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px" }}><input value={l.product_name} onChange={e => { const n = [...lines]; n[i].product_name = e.target.value; setLines(n); }} style={{ ...S.input, padding: "6px 8px" }} /></td>
                    <td style={{ padding: "4px 8px", width: 80 }}><input type="number" min="1" value={l.quantity_ordered} onChange={e => { const n = [...lines]; n[i].quantity_ordered = e.target.value; setLines(n); }} style={{ ...S.input, padding: "6px 8px" }} /></td>
                    <td style={{ padding: "4px 8px", width: 120 }}><input type="number" min="0" step="0.01" value={l.unit_price} onChange={e => { const n = [...lines]; n[i].unit_price = e.target.value; setLines(n); }} style={{ ...S.input, padding: "6px 8px" }} /></td>
                    <td style={{ padding: "4px 8px", width: 80 }}><select value={l.tva_rate} onChange={e => { const n = [...lines]; n[i].tva_rate = e.target.value; setLines(n); }} style={{ ...S.input, padding: "6px 8px" }}><option value="20">20%</option><option value="10">10%</option><option value="5.5">5.5%</option><option value="0">0%</option></select></td>
                    <td style={{ padding: "4px 8px" }}><button onClick={() => setLines(l => l.filter((_, j) => j !== i))} style={{ ...S.btn, padding: "4px 8px", background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => setLines(l => [...l, { product_name: "", quantity_ordered: 1, unit_price: 0, tva_rate: 20 }])} style={{ marginTop: 10, ...S.btn, background: "rgba(26,158,114,0.15)", color: "#1a9e72", fontSize: 12 }}>+ Ajouter une ligne</button>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginBottom: 20 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#4b5563" }}>Total HT : <span style={{ color: "#111827", fontWeight: 700 }}>{fmt(totalHT)} €</span></div>
              <div style={{ fontSize: 12, color: "#4b5563" }}>TVA : <span style={{ color: "#111827", fontWeight: 700 }}>{fmt(totalTVA)} €</span></div>
              <div style={{ fontSize: 15, color: "#1a9e72", fontWeight: 800 }}>TTC : {fmt(totalHT + totalTVA)} €</div>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 6 }}>Notes</label>
            <textarea value={form.notes} onChange={e => setForm(o => ({ ...o, notes: e.target.value }))} rows={3} style={{ ...S.input, resize: "vertical" }} />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={submitOrder} disabled={submitting} style={{ ...S.btn, background: "#1a9e72", color: "#fff", opacity: submitting ? 0.6 : 1 }}>{submitting ? "Création…" : "✅ Créer la commande"}</button>
            <button onClick={() => setTab("dashboard")} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Annuler</button>
          </div>
        </div>
      )}

      {/* ── Tab: Excel Import ── */}
      {tab === "excel" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", color: "#111827" }}>Import Excel</h3>
          <div style={{ color: "#4b5563", fontSize: 13, marginBottom: 24 }}>Importez vos commandes depuis un fichier Excel. Téléchargez le modèle pour connaître le format attendu.</div>

          <button onClick={() => downloadProtectedFile(api.customerOrders.templateDownload)} style={{ ...S.btn, background: "#185fa5", color: "#fff", marginBottom: 24 }}>⬇️ Télécharger le modèle Excel</button>

          <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setImportFile(e.dataTransfer.files[0]); }}
            onClick={() => document.getElementById("excel-input").click()}
            style={{ border: "2px dashed rgba(26,158,114,0.4)", borderRadius: 12, padding: 40, textAlign: "center", cursor: "pointer", marginBottom: 20, background: importFile ? "rgba(26,158,114,0.08)" : "transparent" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
            <div style={{ color: "#111827", fontWeight: 700, marginBottom: 6 }}>{importFile ? importFile.name : "Glissez votre fichier Excel ici"}</div>
            <div style={{ color: "#4b5563", fontSize: 12 }}>Formats acceptés : .xlsx, .xls</div>
            <input id="excel-input" type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={e => setImportFile(e.target.files[0])} />
          </div>

          {importFile && (
            <button onClick={uploadImport} style={{ ...S.btn, background: "#1a9e72", color: "#fff", marginBottom: 16 }}>📥 Importer les commandes</button>
          )}

          {importResult && (
            <div style={{ background: "rgba(26,158,114,0.1)", border: "1px solid rgba(26,158,114,0.3)", borderRadius: 10, padding: 16 }}>
              <div style={{ color: "#1a9e72", fontWeight: 700, marginBottom: 8 }}>✅ Import terminé</div>
              <div style={{ color: "#111827", fontSize: 13 }}>{importResult.imported || 0} commandes importées · {importResult.errors || 0} erreurs</div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: OCR PDF ── */}
      {tab === "ocr" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", color: "#111827" }}>Analyse OCR d'un bon de commande PDF</h3>
          <div style={{ color: "#4b5563", fontSize: 13, marginBottom: 24 }}>Importez un PDF de bon de commande. L'IA extrait automatiquement les informations.</div>

          {!ocrResult && (
            <>
              <div onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); setOcrFile(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById("ocr-input").click()}
                style={{ border: "2px dashed rgba(139,92,246,0.4)", borderRadius: 12, padding: 40, textAlign: "center", cursor: "pointer", marginBottom: 20, background: ocrFile ? "rgba(139,92,246,0.08)" : "transparent" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🤖</div>
                <div style={{ color: "#111827", fontWeight: 700, marginBottom: 6 }}>{ocrFile ? ocrFile.name : "Glissez votre PDF ici"}</div>
                <div style={{ color: "#4b5563", fontSize: 12 }}>Format accepté : PDF uniquement</div>
                <input id="ocr-input" type="file" accept=".pdf" style={{ display: "none" }} onChange={e => setOcrFile(e.target.files[0])} />
              </div>
              {ocrFile && <button onClick={uploadOCR} style={{ ...S.btn, background: "#8b5cf6", color: "#fff" }}>🤖 Analyser le PDF</button>}
            </>
          )}

          {ocrForm && (
            <div>
              <div style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 10, padding: 12, marginBottom: 20, color: "#a78bfa", fontSize: 13 }}>
                ✅ Extraction réussie — vérifiez et corrigez les données avant de confirmer.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                {[
                  { label: "Client", key: "client_name" },
                  { label: "Email", key: "client_email" },
                  { label: "Date commande", key: "order_date" },
                  { label: "Livraison prévue", key: "expected_delivery" },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>{f.label}</label>
                    <input value={ocrForm[f.key] || ""} onChange={e => setOcrForm(o => ({ ...o, [f.key]: e.target.value }))} style={S.input} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={confirmOCR} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>✅ Confirmer et créer la commande</button>
                <button onClick={() => { setOcrFile(null); setOcrResult(null); setOcrForm(null); }} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>🔄 Recommencer</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
